import {
  captureDiagnosisLexiconId,
  type CaptureDiagnosis,
  type NoteFeedback,
  type PhraseFeedback,
} from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import { useEffect, useRef } from 'react'
import type { FC } from 'react'
import { spokenText } from '../theory-copy/spoken-text.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import { formatCaptureReadout } from './capture-readout.js'
import { useVocalCopy } from './use-vocal-copy.js'
import styles from './pitch-feedback-panel.module.css'

/**
 * Everything the tool says about a take.
 *
 * Not one sentence here is composed by this file. Each verdict is one §12 lexicon entry, chosen
 * by `toPhraseFeedback` from the numbers `VocalLib` measured, and rendered with its `{{cents}}`
 * filled through the never-leak-raw-token guard. The only strings this component owns are
 * button labels.
 *
 * Order is the message: §5.5's drift headline comes first when it fires, because "your intervals
 * were right and the whole thing settled low" is a completely different diagnosis from "you sing
 * out of tune", and a singer who reads the per-note chips first will have concluded the latter
 * before they reach it.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.4-§5.7, §9, §12
 */

interface CopyLineProps {
  lexiconId: string
  /** The entry's `{{tokens}}`: cents, a count, a transposition distance. Filled by the lexicon, never prefixed here. */
  tokens?: Record<string, string | number>
  className: string
}

const CopyLine: FC<CopyLineProps> = ({ lexiconId, tokens = {}, className }) => {
  const copy = useVocalCopy(lexiconId, tokens)
  if (!copy) {
    return null
  }
  return (
    <p className={className}>
      <TheoryGlyphs>{copy.body}</TheoryGlyphs>
    </p>
  )
}

interface NoteChipProps {
  note: NoteFeedback
  position: number
}

const NoteChip: FC<NoteChipProps> = ({ note, position }) => {
  const band = useVocalCopy(note.bandLexiconId, { cents: note.cents })
  const stability = useVocalCopy(note.stabilityLexiconId)
  return (
    <li className={styles.chip}>
      <span className={styles.chipIndex}>{position + 1}</span>
      {band ? (
        <span className={styles.chipBand} title={spokenText(band.body)}>
          <TheoryGlyphs>{band.tag}</TheoryGlyphs>
        </span>
      ) : null}
      {/* The sentence (with its cents) reaches keyboard, touch and screen-reader users; `title` alone reaches a mouse */}
      {band ? <span className={styles.visuallyHidden}>{spokenText(band.body)}</span> : null}
      {stability ? (
        <span className={styles.chipStability} title={spokenText(stability.body)}>
          <TheoryGlyphs>{stability.tag}</TheoryGlyphs>
        </span>
      ) : null}
    </li>
  )
}

export interface PitchFeedbackPanelProps {
  feedback: PhraseFeedback
  /** Why nothing was heard, when nothing was. Null when the capture was never diagnosed. */
  diagnosis: CaptureDiagnosis | null
  onRetry: () => void
  onNext: () => void
}

export const PitchFeedbackPanel: FC<PitchFeedbackPanelProps> = props => {
  const { feedback, diagnosis, onRetry, onNext } = props

  /**
   * The button that ran the take unmounted with it, so focus fell to the page. Land it on the verdict instead:
   * a keyboard user continues from here, and a screen reader reads the result out.
   */
  const verdictRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    verdictRef.current?.focus()
  }, [])

  if (feedback.nothingHeard) {
    /**
     * §9: a first-class outcome, not an error path — and deliberately scoreless. A silent user
     * handed a number is being told their microphone works when it does not.
     *
     * Which sentence depends on what the frames actually showed: a dead input device, a voice
     * below the level gate, and a voice too breathy for the tracker are three different problems,
     * and "we didn't hear anything" sends all three to the same dead end. The readout under it is
     * always shown, because this is the one screen in the tool whose verdict cannot be checked
     * against anything unless it prints its numbers.
     */
    return (
      <section className={styles.feedback} aria-label="Feedback">
        <div ref={verdictRef} tabIndex={-1} className={styles.verdict}>
          <CopyLine lexiconId={captureDiagnosisLexiconId(diagnosis)} className={styles.headline} />
          {diagnosis ? <p className={styles.readout}>{formatCaptureReadout(diagnosis)}</p> : null}
        </div>
        <div className={styles.actions}>
          <Button variant="contained" color="primary" onClick={onRetry}>
            Retry
          </Button>
          <Button variant="text" onClick={onNext}>
            Skip
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section className={styles.feedback} aria-label="Feedback">
      <div ref={verdictRef} tabIndex={-1} className={styles.verdict}>
        {feedback.headlineLexiconId ? (
          <CopyLine
            lexiconId={feedback.headlineLexiconId}
            tokens={feedback.headlineTokens}
            className={styles.headline}
          />
        ) : null}

        {feedback.octaveLexiconId ? <CopyLine lexiconId={feedback.octaveLexiconId} className={styles.body} /> : null}

        <ol className={styles.chips}>
          {feedback.perNote.map((note, position) => (
            <NoteChip key={position} note={note} position={position} />
          ))}
        </ol>

        {feedback.missedCount > 0 ? (
          <CopyLine lexiconId="vocal-missed-note" className={styles.body} tokens={{ count: feedback.missedCount }} />
        ) : null}
        {feedback.extraCount > 0 ? (
          <CopyLine lexiconId="vocal-extra-note" className={styles.body} tokens={{ count: feedback.extraCount }} />
        ) : null}

        {feedback.scorePercent === null ? null : (
          <div className={styles.score} role="status" aria-label="Phrase score">
            {feedback.scorePercent}%
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <Button variant="text" onClick={onRetry}>
          Retry
        </Button>
        <Button variant="contained" color="primary" onClick={onNext}>
          Next
        </Button>
      </div>
    </section>
  )
}
